import { TypedDocumentNode, gql } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { rivals } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { DoubleAnimatedDiamond } from 'components/rivals/Arena/ArenaStory/ArenaStoryElement/DoubleAnimatedDiamond';
import { HeaderTeamsMatchup } from 'components/rivals/HeaderTeamsMatchup';

import { ArenaJoined_game } from './__generated__/index.graphql';

const fadeOut = keyframes`
  to {
    opacity: 0;
    display: none;
  }
`;
const Root = styled(Horizontal).attrs({ center: true })`
  position: absolute;
  z-index: 1;
  inset: 0;

  backdrop-filter: blur(22px);

  animation: 0.2s ${fadeOut} ease-in-out;
  animation-delay: 2s;
`;
const DiamonWrapper = styled.div`
  position: relative;
  width: 100%;
  @media ${tabletAndAbove} {
    width: 30%;
  }
`;
const Wrapper = styled(Vertical).attrs({ center: true })`
  width: 100vw;
  height: 100vh;
  justify-content: space-between;

  & > :first-child {
    flex: 1;
  }
  & > :last-child {
    flex: 5;
  }
`;

type Props = { game?: ArenaJoined_game; running?: boolean; onEnd?: () => void };
export const ArenaJoined = ({ game, running, onEnd: onEndProp }: Props) => {
  const [ended, setEnded] = useState(false);
  const { formatMessage } = useIntl();

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (running) {
      timeout = setTimeout(() => {
        onEndProp?.();
        setEnded(true);
      }, 2200);
    }

    return () => !!timeout && clearTimeout(timeout);
  }, [running, onEndProp]);

  if (!running || ended) {
    return null;
  }

  return (
    <Portal id="full-screen-animation">
      <Root>
        <Wrapper>
          {game && <HeaderTeamsMatchup game={game} noLinks />}
          <DiamonWrapper>
            <DoubleAnimatedDiamond
              fadeAway={false}
              color="var(--c-rgb-rivals-arena)"
              text={formatMessage(rivals.joined)}
            />
          </DiamonWrapper>
        </Wrapper>
      </Root>
    </Portal>
  );
};

ArenaJoined.fragments = {
  game: gql`
    fragment ArenaJoined_game on Game {
      id
      ...HeaderTeamsMatchup_game
    }
    ${HeaderTeamsMatchup.fragments.game}
  ` as TypedDocumentNode<ArenaJoined_game>,
};
